import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { user, profile, loading, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    if (window.confirm('¿Estás seguro de que deseas cerrar sesión?')) {
      const result = await logout();
      if (result.success) {
        setShowUserMenu(false);
        navigate('/');
      }
    }
  };

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/catalogo', label: 'Catálogo' },
    { path: '/subastas', label: 'Subastas' },
    { path: '/bienes-raices', label: 'Bienes Raíces' },
    { path: '/autos', label: 'Autos' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/6860c299-4edf-4247-809c-d0a98bc683e6.png" 
              alt="IslaVende Logo" 
              className="h-12 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-colors whitespace-nowrap ${
                  isScrolled || !isHome
                    ? 'text-gray-700 hover:text-cobalt-500'
                    : 'text-white hover:text-cobalt-200'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {loading ? (
              <div className="w-8 h-8 border-2 border-cobalt-500 border-t-transparent rounded-full animate-spin"></div>
            ) : user && profile ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 bg-cobalt-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {profile.nombre_completo ? profile.nombre_completo.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left">
                    <p className={`text-sm font-medium ${isScrolled || !isHome ? 'text-gray-900' : 'text-white'}`}>
                      {profile.nombre_completo || user.email}
                    </p>
                    <p className={`text-xs ${isScrolled || !isHome ? 'text-gray-500' : 'text-gray-200'}`}>
                      {profile.tipo_perfil === 'admin' ? 'Administrador' : profile.tipo_perfil === 'vendedor' ? 'Vendedor' : 'Comprador'}
                    </p>
                  </div>
                  <i className={`ri-arrow-down-s-line text-lg ${isScrolled || !isHome ? 'text-gray-700' : 'text-white'}`}></i>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                    {profile.tipo_perfil === 'admin' && (
                      <Link
                        to="/admin-portal"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center space-x-3 px-4 py-2.5 text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-shield-star-line text-lg"></i>
                        <span className="text-sm font-medium">Panel Admin</span>
                      </Link>
                    )}
                    {profile.tipo_perfil === 'vendedor' && (
                      <Link
                        to="/dashboard"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-cobalt-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-dashboard-line text-lg"></i>
                        <span className="text-sm font-medium">Mi Panel</span>
                      </Link>
                    )}
                    {profile.tipo_perfil === 'comprador' && (
                      <Link
                        to="/mis-compras"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-cobalt-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-shopping-bag-line text-lg"></i>
                        <span className="text-sm font-medium">Mis Compras</span>
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-line text-lg"></i>
                      <span className="text-sm font-medium">Cerrar Sesión</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/registro"
                  className="px-5 py-2.5 text-sm font-medium text-cobalt-500 bg-white border-2 border-cobalt-500 rounded-lg hover:bg-cobalt-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Vender
                </Link>
                <Link
                  to="/login"
                  className="px-5 py-2.5 text-sm font-medium text-white bg-cobalt-500 rounded-lg hover:bg-cobalt-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Iniciar Sesión
                </Link>
              </>
            )}
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${
              isScrolled || !isHome ? 'text-gray-700' : 'text-white'
            }`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-cobalt-50 hover:text-cobalt-500 rounded-lg transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 space-y-2 border-t border-gray-200">
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="w-8 h-8 border-2 border-cobalt-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : user && profile ? (
                <>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-cobalt-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {profile.nombre_completo ? profile.nombre_completo.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {profile.nombre_completo || user.email}
                        </p>
                        <p className="text-xs text-gray-500">
                          {profile.tipo_perfil === 'vendedor' ? 'Vendedor' : 'Comprador'}
                        </p>
                      </div>
                    </div>
                  </div>
                  {profile.tipo_perfil === 'admin' && (
                    <Link
                      to="/admin-portal"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-2.5 text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-shield-star-line text-lg"></i>
                      <span className="text-sm font-medium">Panel Admin</span>
                    </Link>
                  )}
                  {profile.tipo_perfil === 'vendedor' && (
                    <Link
                      to="/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-cobalt-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-dashboard-line text-lg"></i>
                      <span className="text-sm font-medium">Mi Panel</span>
                    </Link>
                  )}
                  {profile.tipo_perfil === 'comprador' && (
                    <Link
                      to="/mis-compras"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-cobalt-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-shopping-bag-line text-lg"></i>
                      <span className="text-sm font-medium">Mis Compras</span>
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-logout-box-line text-lg"></i>
                    <span className="text-sm font-medium">Cerrar Sesión</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/registro"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full px-5 py-2.5 text-sm font-medium text-cobalt-500 bg-white border-2 border-cobalt-500 rounded-lg hover:bg-cobalt-50 transition-colors whitespace-nowrap text-center cursor-pointer"
                  >
                    Vender
                  </Link>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full px-5 py-2.5 text-sm font-medium text-white bg-cobalt-500 rounded-lg hover:bg-cobalt-600 transition-colors whitespace-nowrap text-center cursor-pointer"
                  >
                    Iniciar Sesión
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;